import {
  fetch_configured_onboarding_entity_types,
  fetch_onboarding_forms_for_entity_type
} from '@/services/onboarding_journey_service';
import { fetch_user_roles } from '@/services/workflow_service';
import { get_available_roles_from_response } from './my_queue_helpers';
import {
  build_current_option,
  build_normalized_owner_role,
  build_option_label,
  build_owner_role_names,
  build_template_label,
  build_template_names,
  build_toggled_id_list,
  find_option_id_by_name,
  split_template_names,
  unique_named_options
} from './my_queue_edit_helpers';

export const myQueueEditDropdownMethods = {
  async load_edit_dropdown_options(case_item = {}) {
    await this.load_edit_journey_options(case_item);
    await this.load_edit_templates_for_journey(case_item);
  },
  async load_edit_journey_options(case_item = {}) {
    const current_option = build_current_option(this.edit_form.journey, case_item?.entity_type_id);
    const fetched_journeys = await fetch_configured_onboarding_entity_types().catch(() => []);
    const options = fetched_journeys.map((journey) => ({
      id: journey.id,
      name: journey.name,
      sub_entity_type_id: journey.sub_entity_type_id
    }));
    this.available_edit_journeys = unique_named_options([
      ...(current_option ? [current_option] : []),
      ...options
    ]);
  },
  get_selected_edit_journey(case_item = {}) {
    const current_label = String(this.edit_form.journey || '').trim().toLowerCase();
    const current_id = String(case_item?.entity_type_id || '').trim();
    return this.available_edit_journeys.find((journey) => (
      String(journey.name || '').trim().toLowerCase() === current_label
      || String(journey.id || '') === current_id
    )) || {};
  },
  async load_edit_templates_for_journey(case_item = {}) {
    const selected_journey = this.get_selected_edit_journey(case_item);
    const raw_entity_type_id = selected_journey?.id || case_item?.entity_type_id;
    const entity_type_id = Number(raw_entity_type_id) > 0 ? raw_entity_type_id : '';
    const current_options = split_template_names(this.edit_form.template_name)
      .map((name) => build_current_option(name))
      .filter(Boolean);
    const forms = await fetch_onboarding_forms_for_entity_type(entity_type_id, {
      sub_entity_type_id: selected_journey?.sub_entity_type_id
    }).catch(() => []);
    const options = forms.map((form) => ({ id: form.id, name: form.name }));
    this.available_edit_templates = unique_named_options([
      ...current_options,
      ...options
    ]);
    this.sync_edit_template_selection();
  },
  sync_edit_template_selection() {
    const existing_ids = this.selected_edit_template_ids();
    if (existing_ids.length) {
      const valid_ids = new Set(this.available_edit_templates.map((template) => String(template.id)));
      this.edit_form.template_ids = existing_ids.filter((template_id) => valid_ids.has(template_id));
      this.edit_form.template_name = build_template_names(this.selected_edit_templates());
      return;
    }
    const selected_names = split_template_names(this.edit_form.template_name);
    const selected_ids = selected_names
      .map((name) => find_option_id_by_name(this.available_edit_templates, name))
      .filter(Boolean);
    this.edit_form.template_ids = selected_ids;
    this.edit_form.template_name = build_template_names(this.selected_edit_templates());
  },
  async handle_edit_journey_change() {
    this.edit_form.template_name = '';
    this.edit_form.template_ids = [];
    this.edit_template_search = '';
    await this.load_edit_templates_for_journey();
  },
  selected_edit_template_ids() {
    return Array.isArray(this.edit_form.template_ids)
      ? this.edit_form.template_ids.map((template_id) => String(template_id)).filter(Boolean)
      : [];
  },
  selected_edit_templates() {
    const selected_ids = this.selected_edit_template_ids();
    return this.available_edit_templates.filter((template) => selected_ids.includes(String(template.id)));
  },
  filtered_edit_templates() {
    const search = String(this.edit_template_search || '').trim().toLowerCase();
    if (!search) return this.available_edit_templates;
    return this.available_edit_templates.filter((template) => (
      String(template?.name || '').trim().toLowerCase().includes(search)
    ));
  },
  edit_template_label() {
    return build_template_label(this.selected_edit_templates()) || 'Select template';
  },
  is_edit_template_selected(template_id) {
    return this.selected_edit_template_ids().includes(String(template_id));
  },
  toggle_edit_template_dropdown() {
    this.show_edit_template_dropdown = !this.show_edit_template_dropdown;
    this.show_edit_owner_dropdown = false;
  },
  toggle_edit_template(template = {}) {
    const template_id = String(template?.id || '').trim();
    if (!template_id) return;
    const selected_ids = new Set(this.selected_edit_template_ids());
    if (selected_ids.has(template_id)) {
      selected_ids.delete(template_id);
    } else {
      selected_ids.add(template_id);
    }
    this.edit_form.template_ids = Array.from(selected_ids);
    this.edit_form.template_name = build_template_names(this.selected_edit_templates());
  },
  normalize_owner_role(role) {
    const normalized_role = build_normalized_owner_role(role);
    return normalized_role;
  },
  async load_available_owner_roles() {
    const response = await fetch_user_roles().catch(() => null);
    if (!response) {
      this.available_owner_roles = [];
      return;
    }

    const roles = get_available_roles_from_response(response)
      .map((role) => this.normalize_owner_role(role))
      .filter((role) => role.id && role.name);

    this.available_owner_roles = roles;
    this.sync_edit_owner_role_selection();
  },
  sync_edit_owner_role_selection() {
    const existing_ids = this.selected_edit_owner_role_ids();
    if (existing_ids.length) {
      const valid_ids = new Set(this.available_owner_roles.map((role) => String(role.id)));
      this.edit_form.owner_role_ids = existing_ids.filter((role_id) => valid_ids.has(role_id));
      this.edit_form.owner_role_id = this.edit_form.owner_role_ids[0] || '';
      this.edit_form.owner_role_name = this.selected_edit_owner_roles().map((role) => role.name).join(', ');
      return;
    }
    const selected_role = this.available_owner_roles.find((role) => role.name === this.edit_form.owner_role_name);
    if (!selected_role) return;
    this.edit_form.owner_role_id = String(selected_role.id);
    this.edit_form.owner_role_ids = [String(selected_role.id)];
  },
  selected_edit_owner_role_ids() {
    return Array.isArray(this.edit_form.owner_role_ids)
      ? this.edit_form.owner_role_ids.map((role_id) => String(role_id)).filter(Boolean)
      : [];
  },
  selected_edit_owner_roles() {
    const selected_ids = this.selected_edit_owner_role_ids();
    return this.available_owner_roles.filter((role) => selected_ids.includes(String(role.id)));
  },
  filtered_edit_owner_roles() {
    const search = String(this.edit_owner_role_search || '').trim().toLowerCase();
    if (!search) return this.available_owner_roles;
    return this.available_owner_roles.filter((role) => (
      String(role?.name || '').trim().toLowerCase().includes(search)
    ));
  },
  edit_owner_role_label() {
    return build_option_label(this.selected_edit_owner_roles(), 'role') || 'Select role';
  },
  is_edit_owner_role_selected(role_id) {
    return this.selected_edit_owner_role_ids().includes(String(role_id));
  },
  toggle_edit_owner_dropdown() {
    this.show_edit_owner_dropdown = !this.show_edit_owner_dropdown;
    this.show_edit_template_dropdown = false;
  },
  toggle_edit_owner_role(role = {}) {
    const role_id = String(role?.id || '').trim();
    if (!role_id) return;
    this.edit_form.owner_role_ids = build_toggled_id_list(this.selected_edit_owner_role_ids(), role_id);
    this.edit_form.owner_role_id = this.edit_form.owner_role_ids[0] || '';
    this.edit_form.owner_role_name = build_owner_role_names(this.selected_edit_owner_roles());
  }
};
